import type { ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';
import { produce } from 'immer';

import type { SerializableNode, SerializerProps } from '@/serializer';
import { updateNodeText } from '@/utils/updateNodeText';

import { getStructureElement } from '../../serializer/getStructureElement';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf';
import { TESTIMONIAL_FIELD_MAP, type Testimonial } from '../Testimonials.types';

interface ExtendedSerializerProps extends SerializerProps {
  testimonial: Testimonial;
  node: JSONContent & SerializableNode;
}

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    testimonial,
  }: ExtendedSerializerProps,
  path = [0], // internal path tracking
): ReactNode => {
  const key = createKey([node, path]);

  const isImage = node?.type === 'image';
  const isParagraph = node?.type === 'paragraph';
  const isHeading = node?.type === 'heading';
  const mergeTag = node?.attrs?.mergeTag;
  const isRootContainerImage = node?.type === 'container' && rootStyle && node?.attrs?.backgroundImage;

  if (isRootContainerImage) {
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        backgroundImage: testimonial?.picture_url,
      },
    } as JSONContent & SerializableNode;
  }

  // Swap out images
  if (isImage && mergeTag) {
    if (mergeTag === 'testimonial.image') {
      node = produce(node, (draft) => {
        if (draft.attrs) {
          draft.attrs.src = testimonial?.picture_url;
          draft.attrs.alt = testimonial?.name;
        }
      });
    }
  }

  // Swap out headings && paragraphs
  if ((isParagraph || isHeading) && mergeTag) {
    if (mergeTag in TESTIMONIAL_FIELD_MAP) {
      const fieldKey = TESTIMONIAL_FIELD_MAP[mergeTag];
      const newText = testimonial?.[fieldKey];
      node = updateNodeText(node, newText) as JSONContent & SerializableNode;
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          testimonial,
        },
        [...path, i],
      ),
    ) || [];

  return getStructureElement({
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    key,
    children: [...nodeChildren, children],
    jsonPath: path,
  });
};

export function TestimonialsSerializer(props: ExtendedSerializerProps) {
  return <>{serializeNode(props)}</>;
}
